import { useState } from "react";
import { MessageSquare, Globe2, Clock3, Loader2, AlertTriangle, Pin } from "lucide-react";
import { cn } from "@/lib/utils";
import { HubAuthorDisplay, type HubAuthor } from "@/components/social/HubAuthorDisplay";
import { HubComments } from "@/components/social/HubComments";
import { HubReactionButton, HubReactionSummary } from "@/components/social/HubReactionBar";
import { HubItemMenu } from "@/components/social/HubItemMenu";
import { HubInlineEditor } from "@/components/social/HubInlineEditor";
import { MAX_HUB_BODY } from "@/components/social/HubComposer";

import { hubReactionTotal, type HubReactionCounts, type HubReactionType } from "@/lib/hub/reactions";
import { relativeTime } from "@/lib/hub/relativeTime";
import type { HubPendingPost, HubPost } from "@/hooks/hub/useHubFeed";

/**
 * LR HUB feed post.
 *
 * SAFETY: the body is rendered as PLAIN TEXT (React escaping + whitespace
 * preservation). Never use dangerouslySetInnerHTML here.
 *
 * Author identity always goes through HubAuthorDisplay (the single identity
 * surface). Like/comment counts are static in this stage.
 */

const TYPE_LABEL: Record<string, string> = {
  capacity_update: "Capacity Update",
  lane_request: "Lane Request",
  shipment_need: "Shipment Need",
  poll: "Poll",
  general: "Update",
};

export { relativeTime } from "@/lib/hub/relativeTime";

function Shell({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <section
      className={cn("rounded-xl border border-border bg-card p-4", className)}
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      {children}
    </section>
  );
}

function Body({ text }: { text: string }) {
  return (
    <p className="mt-3 whitespace-pre-wrap break-words text-[12.5px] leading-relaxed text-foreground">
      {text}
    </p>
  );
}

function Tags({ tags }: { tags: string[] }) {
  if (!tags?.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-1.5">
      {tags.map((t) => (
        <span
          key={t}
          className="inline-flex items-center rounded-md bg-brand-teal-subtle px-2 py-1 text-[11px] font-medium text-brand-teal-deep"
        >
          {t}
        </span>
      ))}
    </div>
  );
}

/** Subtle uppercase feed-label pill (e.g. "LANERUNNER NEWS"). */
function FeedPill({ label }: { label?: string | null }) {
  if (!label) return null;
  return (
    <span className="inline-flex items-center rounded-md bg-brand-teal-subtle px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-brand-teal-deep">
      {label}
    </span>
  );
}

/** Optimistic placeholder shown at the very top the instant the user posts. */
export function HubPendingPostCard({
  post,
  author,
  onDismiss,
}: {
  post: HubPendingPost;
  author: HubAuthor;
  onDismiss?: () => void;
}) {
  const failed = post.state === "error";
  return (
    <Shell className={cn(failed ? "border-danger/40" : "border-brand-teal/30 opacity-90")}>
      <HubAuthorDisplay
        author={author}
        timeLabel="just now"
        trailing={
          failed ? (
            <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-danger">
              <AlertTriangle className="h-3 w-3" /> Not posted
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-brand-teal-deep">
              <Loader2 className="h-3 w-3 animate-spin" /> posting…
            </span>
          )
        }
      />
      {post.feed_name ? (
        <div className="mt-2">
          <FeedPill label={post.feed_name} />
        </div>
      ) : null}
      <Body text={post.body} />
      <Tags tags={post.tags} />
      {failed ? (
        <div className="mt-3 flex items-start justify-between gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-[11.5px] text-danger">
          <span>{post.error}</span>
          {onDismiss ? (
            <button onClick={onDismiss} className="font-semibold underline">
              Dismiss
            </button>
          ) : null}
        </div>
      ) : null}
    </Shell>
  );
}

export function HubPostCard({
  post,
  author,
  reactions,
  justPosted,
  isOwn,
  saving,
  onEditSave,
  onDeleted,
  feedLabel,
  onPinChanged,
  className,
}: {
  post: HubPost;
  author: HubAuthor;
  /** Reaction state for this post (from useHubReactions in the feed). */
  reactions?: {
    mine: HubReactionType | null;
    counts: HubReactionCounts;
    canReact: boolean;
    onReact: (type: HubReactionType) => void;
  };
  /** Set for the post the user just published — fades/slides it in. */
  justPosted?: boolean;
  /** True when the signed-in user authored this post (kebab menu contents). */
  isOwn?: boolean;
  saving?: boolean;
  /** Routes the edited body back through moderate-hub-post. */
  onEditSave?: (body: string) => Promise<boolean>;
  onDeleted?: () => void;
  /** Feed name for the label pill (resolved by the feed, joined or by id). */
  feedLabel?: string | null;
  /** Called after an admin pins/unpins so the feed can refetch. */
  onPinChanged?: () => void;
  /** Optional wrapper class — used by the feed for alternating row shading. */
  className?: string;
}) {
  const inReview = post.moderation_status !== "approved";
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const total = hubReactionTotal(reactions?.counts);

  return (
    <Shell
      className={cn(
        inReview ? "border-brand-amber/40" : undefined,
        justPosted && "animate-slide-in-from-top",
        className,
      )}
    >
      <HubAuthorDisplay
        author={author}
        timeLabel={`${relativeTime(post.created_at)}${post.edited_at ? " · edited" : ""}`}
        trailing={
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Globe2 className="h-3 w-3" />
            <HubItemMenu
              kind="post"
              targetId={post.id}
              isOwn={!!isOwn}
              authorId={post.author_id}
              authorLabel={author?.hubIdentity?.handle ? `@${author.hubIdentity.handle}` : author?.name}
              onEdit={() => setEditing(true)}
              onDeleted={() => onDeleted?.()}
              pinned={!!post.pinned_at}
              onPinChanged={() => onPinChanged?.()}
            />
          </div>
        }
      />

      {feedLabel || post.pinned_at ? (
        <div className="mt-2 flex items-center gap-1.5">
          <FeedPill label={feedLabel} />
          {post.pinned_at ? (
            <span className="inline-flex items-center gap-1 rounded-md bg-brand-amber/15 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-foreground">
              <Pin className="h-2.5 w-2.5" /> Pinned
            </span>
          ) : null}
        </div>
      ) : null}

      {inReview ? (
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-brand-amber/15 px-2 py-1 text-[10.5px] font-semibold text-foreground">
          <Clock3 className="h-3 w-3" />
          {post.moderation_status === "rejected"
            ? "Not approved — only you can see this."
            : "Pending approval — only you can see this until the LaneRunner team approves it."}
        </div>
      ) : null}


      {editing ? (
        <HubInlineEditor
          initial={post.body}
          max={MAX_HUB_BODY}
          saving={saving}
          onCancel={() => setEditing(false)}
          onSave={async (body) => {
            const ok = await onEditSave?.(body);
            if (ok) setEditing(false);
          }}
        />
      ) : (
        <Body text={post.body} />
      )}

      <Tags tags={post.tags} />

      {total > 0 ? (
        <div className="mt-3 flex items-center justify-between gap-3">
          <HubReactionSummary counts={reactions!.counts} />
        </div>
      ) : null}

      <div className="mt-3.5 flex items-center justify-between gap-3 border-t border-border pt-2.5">
        <div className="flex items-center gap-3 text-[11.5px] font-semibold text-muted-foreground">
          {reactions ? (
            <HubReactionButton
              mine={reactions.mine}
              disabled={!reactions.canReact}
              onReact={reactions.onReact}
            />
          ) : null}
          <button
            type="button"
            onClick={() => setCommentsOpen((v) => !v)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 transition-colors",
              commentsOpen ? "text-brand-teal-deep" : "hover:text-brand-teal-deep",
            )}
          >
            <MessageSquare className="h-3.5 w-3.5" /> {post.comment_count}
          </button>
        </div>
        <span className="rounded-md bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          {TYPE_LABEL[post.content_type] ?? "Update"}
        </span>
      </div>

      <HubComments postId={post.id} open={commentsOpen} />
    </Shell>
  );
}

export default HubPostCard;
